import {
  type MutableRefObject,
  type ReactElement,
  type RefObject,
  useEffect,
  useRef,
  useState
} from 'react'
import { stickyScrollbar, stickyScrollbarContent } from './DataTable.css'

type DataTableStickyScrollbarProps = {
  scrollRef: RefObject<HTMLDivElement | null>
  barRef: MutableRefObject<HTMLDivElement | null>
  contentWidth: number
  onVisibilityChange?: (visible: boolean) => void
}

const DataTableStickyScrollbar = ({
  scrollRef,
  barRef,
  contentWidth,
  onVisibilityChange
}: DataTableStickyScrollbarProps): ReactElement | null => {
  const [visible, setVisible] = useState(false)
  const onVisibilityChangeRef = useRef(onVisibilityChange)
  onVisibilityChangeRef.current = onVisibilityChange

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    const update = () => {
      const next = scrollEl.scrollWidth > scrollEl.clientWidth + 1
      setVisible(next)
      onVisibilityChangeRef.current?.(next)
    }

    update()
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(scrollEl)
    if (scrollEl.firstElementChild) {
      resizeObserver.observe(scrollEl.firstElementChild)
    }
    window.addEventListener('resize', update)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [scrollRef])

  if (!visible) return null

  return (
    <div
      ref={(node) => {
        barRef.current = node
      }}
      className={stickyScrollbar}
      aria-hidden
    >
      <div
        className={stickyScrollbarContent}
        style={{ width: Math.max(contentWidth, 0) }}
      />
    </div>
  )
}

export default DataTableStickyScrollbar
